/**
 * SharedParentReport — public, no-login view of a parent report
 * snapshot. Loads `/api/shortlist/parent-report/snapshot/:slug`,
 * which is a frozen copy of the student's shortlist + applicant
 * profile at share time. Reuses the DegreeReportCard / RollupTile
 * renderers from `ShortlistParentReport.jsx` so the layout stays
 * identical between the private and public URLs.
 *
 * Design principles:
 *   • No auth required — the slug in the URL is the capability.
 *   • Snapshots have a 30-day TTL; expired links return 404.
 *   • No "Share" or "Edit" toolbar — the recipient is read-only.
 *   • Print CSS still active so parents can save-as-PDF the same way.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import {
  DegreeReportCard,
  RollupTile,
  useShortlistChances,
  fmtMoney,
  CompareTable,
} from "@/pages/ShortlistParentReport";
import { rollupCosts, rollupCostLabel } from "@/lib/courseTuition";

export default function SharedParentReport() {
  const { slug } = useParams();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [compareSelected, setCompareSelected] = useState(() => new Set());

  const toggleCompare = (degreeId) => {
    setCompareSelected((prev) => {
      const next = new Set(prev);
      if (next.has(degreeId)) next.delete(degreeId);
      else if (next.size >= 3) {
        toast.info("You can compare up to 3 courses at a time");
        return prev;
      } else {
        next.add(degreeId);
      }
      return next;
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/shortlist/parent-report/snapshot/${slug}`,
        );
        setSnapshot(data);
      } catch (e) {
        setError(
          e?.response?.status === 404
            ? "This link has expired or was never valid."
            : "Could not load the report.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  // Recompute per-row admission probabilities client-side from the
  // snapshot's applicant profile. We deliberately DO NOT freeze the
  // probability numbers into the snapshot itself — students may
  // update grades between save + share, and the recipient should
  // see the same numbers the site currently shows.
  const chances = useShortlistChances(
    snapshot?.items || [],
    snapshot?.applicant_profile || null,
  );

  const rollup = useMemo(() => {
    if (!snapshot?.items?.length) return null;
    const items = snapshot.items;
    const costRoll = rollupCosts(items);
    const employerSet = new Set();
    for (const it of items) {
      const emps = it.degree?.outcomes?.top_employers
        || it.degree?.outcomes?.destinations?.top_employers
        || [];
      for (const e of emps.slice(0, 3)) {
        employerSet.add(typeof e === "string" ? e : e.name);
      }
    }
    return {
      count: items.length,
      countries: new Set(items.map(it => it.university?.country).filter(Boolean)).size,
      ...costRoll,
      topEmployers: [...employerSet].slice(0, 8),
    };
  }, [snapshot]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center text-stone-500">
        Loading shared report…
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="shared-report-error">
        <div className="label-uppercase">Link no longer available</div>
        <p className="mt-2 text-stone-600">{error}</p>
        <Link to="/">
          <Button className="mt-6" variant="outline">Back to home</Button>
        </Link>
      </div>
    );
  }

  const owner = snapshot?.owner_name || snapshot?.owner_email || "Student";
  const profile = snapshot?.applicant_profile || {};

  return (
    <div className="parent-report-root max-w-4xl mx-auto px-6 py-10">
      {/* Read-only toolbar — no Share button (that's the sharer's job). */}
      <div className="print:hidden flex items-center justify-between mb-6">
        <div className="text-[11px] font-underground-sc tracking-wider text-stone-500">
          Shared parent-ready report · read-only
        </div>
        <Button
          onClick={() => window.print()}
          className="bg-[#4C1D95] hover:bg-[#3d1479] text-white"
          data-testid="shared-report-print-btn"
        >
          <Printer className="h-4 w-4 mr-2" /> Print / Save as PDF
        </Button>
      </div>

      <div className="border-b-2 border-stone-900 pb-6 mb-8" data-testid="shared-report-cover">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              Which University Advisor · Family-ready shortlist report
            </div>
            <h1 className="font-editorial text-4xl text-stone-900 leading-tight">
              {owner}'s shortlist
            </h1>
            {Object.keys(profile).length > 0 && (
              <div className="mt-2 text-sm text-stone-700">
                {profile.curriculum?.toUpperCase() || "Applicant"}
                {profile.a_level && ` · A-Level ${profile.a_level}`}
                {profile.ib && ` · IB ${profile.ib}`}
                {profile.sat_total && ` · SAT ${profile.sat_total}`}
                {(profile.intended_degrees?.[0] || profile.intended_degree) &&
                  ` · aiming for ${profile.intended_degrees?.[0] || profile.intended_degree}`}
              </div>
            )}
          </div>
          <div className="shrink-0 flex items-center gap-2 text-[#4C1D95]">
            <GraduationCap className="h-8 w-8" />
          </div>
        </div>

        {rollup && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <RollupTile label="Courses saved" value={rollup.count} />
            <RollupTile label="Countries" value={rollup.countries} testId="shared-rollup-countries" />
            <RollupTile
              label="Total cost range"
              value={rollupCostLabel(rollup, fmtMoney)}
              testId="shared-rollup-cost-range"
            />
          </div>
        )}

        {rollup?.topEmployers?.length > 0 && (
          <div className="mt-5">
            <div className="font-mono text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              Employers that hire from this shortlist
            </div>
            <div className="text-sm text-stone-800">{rollup.topEmployers.join(" · ")}</div>
          </div>
        )}
      </div>

      {compareSelected.size >= 2 && (
        <CompareTable
          selectedIds={[...compareSelected]}
          items={snapshot?.items || []}
          chances={chances}
        />
      )}

      <div data-testid="shared-report-degrees">
        {(snapshot?.items || []).map((item, i) => (
          <DegreeReportCard
            key={item.degree_id || item.university_id}
            item={item}
            classification={chances[item.degree_id]}
            index={i}
            onToggleCompare={toggleCompare}
            isSelected={compareSelected.has(item.degree_id)}
          />
        ))}
      </div>

      <div className="mt-10 pt-6 border-t border-stone-200 text-[11px] text-stone-500 leading-relaxed">
        <p>
          Shared by {owner}. Numbers on this report come from the same
          verified sources the site shows: UCAS End-of-Cycle acceptance
          rates, HESA Discover Uni salary data, US College Scorecard,
          and each university's own entry-requirements page.
        </p>
        <p className="mt-2">
          This link expires {snapshot?.expires_at ? new Date(snapshot.expires_at).toLocaleDateString() : "in 30 days"}.
        </p>
      </div>

      <style>{`
        @media print {
          @page { size: A4; margin: 15mm; }
          body { background: white !important; }
          nav, header, footer, .print\\:hidden { display: none !important; }
          .parent-report-root { max-width: none; padding: 0; }
          .parent-report-card {
            page-break-inside: avoid;
            break-inside: avoid;
            box-shadow: none !important;
            background: white !important;
          }
          .break-inside-avoid { page-break-inside: avoid; break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
